import { getFlashcards } from './flashcardsProvider';

export interface CategoryInfo {
  name: string;
  count: number;
  color: string;
  mutedBg: string;
  mutedText: string;
  mutedBgDark: string;
  mutedTextDark: string;
}

export function getCategories(state: string = 'ga'): CategoryInfo[] {
  const flashcards = getFlashcards(state);
  
  // Count cards by category
  const categoryCounts: { [key: string]: number } = {};
  flashcards.forEach(card => {
    categoryCounts[card.category] = (categoryCounts[card.category] || 0) + 1;
  });

  // Color palette for categories (gradient + muted solid for letter badges, + dark mode)
  const colorSets: { gradient: string; mutedBg: string; mutedText: string; mutedBgDark: string; mutedTextDark: string }[] = [
    { gradient: 'from-blue-500 to-blue-600', mutedBg: 'bg-blue-100', mutedText: 'text-blue-700', mutedBgDark: 'bg-blue-900/70', mutedTextDark: 'text-blue-300' },
    { gradient: 'from-indigo-500 to-indigo-600', mutedBg: 'bg-indigo-100', mutedText: 'text-indigo-700', mutedBgDark: 'bg-indigo-900/70', mutedTextDark: 'text-indigo-300' },
    { gradient: 'from-purple-500 to-purple-600', mutedBg: 'bg-purple-100', mutedText: 'text-purple-700', mutedBgDark: 'bg-purple-900/70', mutedTextDark: 'text-purple-300' },
    { gradient: 'from-pink-500 to-pink-600', mutedBg: 'bg-pink-100', mutedText: 'text-pink-700', mutedBgDark: 'bg-pink-900/70', mutedTextDark: 'text-pink-300' },
    { gradient: 'from-red-500 to-red-600', mutedBg: 'bg-red-100', mutedText: 'text-red-700', mutedBgDark: 'bg-red-900/70', mutedTextDark: 'text-red-300' },
    { gradient: 'from-orange-500 to-orange-600', mutedBg: 'bg-orange-100', mutedText: 'text-orange-700', mutedBgDark: 'bg-orange-900/70', mutedTextDark: 'text-orange-300' },
    { gradient: 'from-yellow-500 to-yellow-600', mutedBg: 'bg-yellow-100', mutedText: 'text-yellow-700', mutedBgDark: 'bg-yellow-900/70', mutedTextDark: 'text-yellow-300' },
    { gradient: 'from-green-500 to-green-600', mutedBg: 'bg-green-100', mutedText: 'text-green-700', mutedBgDark: 'bg-green-900/70', mutedTextDark: 'text-green-300' },
    { gradient: 'from-teal-500 to-teal-600', mutedBg: 'bg-teal-100', mutedText: 'text-teal-700', mutedBgDark: 'bg-teal-900/70', mutedTextDark: 'text-teal-300' },
    { gradient: 'from-cyan-500 to-cyan-600', mutedBg: 'bg-cyan-100', mutedText: 'text-cyan-700', mutedBgDark: 'bg-cyan-900/70', mutedTextDark: 'text-cyan-300' },
    { gradient: 'from-emerald-500 to-emerald-600', mutedBg: 'bg-emerald-100', mutedText: 'text-emerald-700', mutedBgDark: 'bg-emerald-900/70', mutedTextDark: 'text-emerald-300' },
    { gradient: 'from-lime-500 to-lime-600', mutedBg: 'bg-lime-100', mutedText: 'text-lime-700', mutedBgDark: 'bg-lime-900/70', mutedTextDark: 'text-lime-300' },
    { gradient: 'from-amber-500 to-amber-600', mutedBg: 'bg-amber-100', mutedText: 'text-amber-700', mutedBgDark: 'bg-amber-900/70', mutedTextDark: 'text-amber-300' },
    { gradient: 'from-rose-500 to-rose-600', mutedBg: 'bg-rose-100', mutedText: 'text-rose-700', mutedBgDark: 'bg-rose-900/70', mutedTextDark: 'text-rose-300' }
  ];

  // Convert to array
  let entries = Object.entries(categoryCounts)
    .map(([name, count], index) => {
      const set = colorSets[index % colorSets.length];
      return {
        name,
        count,
        color: set.gradient,
        mutedBg: set.mutedBg,
        mutedText: set.mutedText,
        mutedBgDark: set.mutedBgDark,
        mutedTextDark: set.mutedTextDark
      };
    });

  // For NY: pin key exam categories first (License Law, Agency, etc.), then interleave rest
  if (state.toLowerCase() === 'ny') {
    const PIN_FIRST = ['NY License Law and Professional Practice', 'NY Agency and Fair Housing', 'NY Brokerage Operations and Transactions', 'NY Property, Ownership, and Land Use', 'NY Finance, Valuation, and Math'];
    const pinned = entries.filter(e => PIN_FIRST.includes(e.name));
    const rest = entries.filter(e => !PIN_FIRST.includes(e.name));
    const ny = rest.filter(e => e.name.startsWith('NY ')).sort((a, b) => b.count - a.count);
    const national = rest.filter(e => !e.name.startsWith('NY ')).sort((a, b) => b.count - a.count);
    const interleaved: typeof entries = [];
    for (let i = 0; i < Math.max(ny.length, national.length); i++) {
      if (i < ny.length) interleaved.push(ny[i]);
      if (i < national.length) interleaved.push(national[i]);
    }
    entries = [...pinned, ...interleaved];
  } else if (state.toLowerCase() === 'nj') {
    const PIN_FIRST = ['NJ License Law and Professional Practice', 'NJ Agency and Fair Housing', 'NJ Brokerage Operations and Transactions', 'NJ Property, Ownership, and Land Use', 'NJ Finance, Valuation, and Math'];
    const pinned = entries.filter(e => PIN_FIRST.includes(e.name));
    const rest = entries.filter(e => !PIN_FIRST.includes(e.name));
    const nj = rest.filter(e => e.name.startsWith('NJ ')).sort((a, b) => b.count - a.count);
    const national = rest.filter(e => !e.name.startsWith('NJ ')).sort((a, b) => b.count - a.count);
    const interleaved: typeof entries = [];
    for (let i = 0; i < Math.max(nj.length, national.length); i++) {
      if (i < nj.length) interleaved.push(nj[i]);
      if (i < national.length) interleaved.push(national[i]);
    }
    entries = [...pinned, ...interleaved];
  } else {
    entries = entries.sort((a, b) => b.count - a.count);
  }

  return entries;
}




